<script setup lang="ts">import { ref as _ref, shallowRef as _shallowRef, computed as _computed } from 'vue';
import { createPropsRestProxy } from '/vue-macros/reactivity-transform/helper';
const props = createPropsRestProxy(__props, ["loadingIndicator","loadingText","href"]);

import { onUnmounted, watch, watchEffect } from 'vue'
import { EventType, Fit, Layout, Rive, RiveFile } from '@rive-app/webgl2'
import { useWindowSize } from '@vueuse/core'
import LoadingIndicator from '@consumer/components/LoadingIndicator.vue'
import { notifyError } from '@shared/errors'
import { isProduction } from '@shared/environment'
import type { Dimensions, RiveOptions, RiveParameters } from './types'

type Props = RiveParameters & RiveOptions

/**
 * Props definition
 *
 */
withDefaults(defineProps<Props>(), { loadingIndicator: true,loadingText: 'Loading...', })

/**
 * Emit defintions
 */
const emit = defineEmits<{ loaded: [Rive, RiveFile]; loadFailed: [any] }>()

/**
 * Template Refs
 */
const canvas = _ref<HTMLCanvasElement>()
const container = _ref<HTMLElement>()
let loading = _ref(true)
let loaded = _ref(false)
let loadFailed = _ref(false)
/**
 * Reactive variables
 */
const { width: wWidth, height: wHeight } = useWindowSize()

let riveInstance = _shallowRef<Rive>()

let dimensions = _ref<Dimensions>({
  width: 0,
  height: 0,
})

const animations = _computed(() => props.animations)

/**
 * Computed Rive Options
 */
const options = _computed(() => {
  return Object.assign({}, defaultOptions, props)
})

/**
 * Default Rive options will be overwritten if options are passed in
 */
const defaultOptions = {
  useDevicePixelRatio: true,
  fitCanvasToArtboardHeight: false,
  useOffscreenRenderer: true,
}

/**
 * Watches windowsize(height and width) and updates the canvas dimensions
 */
watchEffect(() => {
  if (canvas.value && container.value && wWidth.value && wHeight.value) {
    const { width, height } = getCanvasDimensions()

    const boundsChanged
      = width !== dimensions.value.width || height !== dimensions.value.height

    if (
      canvas.value
      && container.value
      && loaded.value
      && boundsChanged
    ) {
      if (options.value.fitCanvasToArtboardHeight)
        container.value.style.height = `${height}px`

      if (options.value.useDevicePixelRatio) {
        const dpr = 2
        canvas.value.width = dpr * width
        canvas.value.height = dpr * height
        canvas.value.style.width = `${width}px`
        canvas.value.style.height = `${height}px`
      }
      else {
        canvas.value.width = width
        canvas.value.height = height
      }
      dimensions.value = { width, height }
      riveInstance.value?.resizeDrawingSurfaceToCanvas()
    }
  }
})

/**
 * watches props.animations and updates the Rive instance
 */
watch(
  () => animations.value,
  () => {
    if (riveInstance.value && animations.value) {
      riveInstance.value.stop(riveInstance.value.animationNames)
      riveInstance.value.play(animations.value)
    }
  },
)

/**
 * Set up IntersectionObserver to stop rendering if the animation is not in
 * view.
 */
watchEffect(() => {
  const observer = new IntersectionObserver(([entry]) => {
    entry.isIntersecting
      ? riveInstance.value && riveInstance.value.play()
      : riveInstance.value && riveInstance.value.pause()
  })

  if (canvas.value)
    observer.observe(canvas.value)

  return () => {
    observer.disconnect()
  }
})

/**
 * gets dimensions of container returns width and height
 */
function getCanvasDimensions () {
  const { width, height }
    = container.value?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0)
  if (riveInstance.value && options.value.fitCanvasToArtboardHeight) {
    const { maxY, maxX } = riveInstance.value.bounds
    return { width, height: width * (maxY / maxX) }
  }
  return { width, height }
}

function onTouchEnd () {
  if (!__props.href) return

  window.location.href = __props.href
}

/**
 * onMounted initializes the Rive instance
 */
watch(
  () => [canvas.value, props.src],
  () => {
    if (riveInstance.value) {
      riveInstance.value.stopRendering()
      riveInstance.value = undefined
    }

    if (canvas.value && props.src) {
      const { useOffscreenRenderer } = options.value
      loading.value = true
      loadFailed.value = false
      const { src, ...restProps } = props

      const layout = props.layout || new Layout({ fit: Fit.Contain })

      if (restProps.riveFile) {
        const rawFile = toRaw(restProps.riveFile)
        const r = new Rive({
          ...restProps,
          riveFile: rawFile,
          layout,
          useOffscreenRenderer,
          canvas: canvas.value,
        })
        r.on(EventType.Load, () => {
          emit('loaded', r, props.riveFile!)
          riveInstance.value = r
          loaded.value = true
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading.value = false
          }, 100)
        })
        r.on(EventType.LoadError, (error) => {
          emit('loadFailed', error)
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading.value = false
          }, 100)
          loadFailed.value = true
          // Rive is very flaky with errors that are causing circle tests to fail randomly.
          if (isProduction) notifyError(error)
        })
      }
      else {
        const file = new RiveFile({
          src,
          onLoad: (e) => {
            const loadedFile = e.data as RiveFile
            const r = new Rive({
              ...restProps,
              riveFile: loadedFile,
              layout,
              useOffscreenRenderer,
              canvas: canvas.value,
            })
            r.on(EventType.Load, () => {
              emit('loaded', r, loadedFile)
              riveInstance.value = r
              setTimeout(() => {
                // allow some time for the first frame to render
                // before removing the loading state
                loading.value = false
              }, 100)
              loaded.value = true
            })
          },
        })
        file.init().catch((error) => {
          emit('loadFailed', error)
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading.value = false
          }, 100)
          loadFailed.value = true
          // Rive is very flaky with errors that are causing circle tests to fail randomly.
          if (isProduction) notifyError(error)
        })
      }
    }
  },
)

onUnmounted(() => {
  if (riveInstance.value) {
    riveInstance.value.stopRendering()
    riveInstance.value = undefined
  }
})

defineExpose({
  riveInstance: riveInstance.value,
})
</script>

<template>
  <section ref="container" class="relative w-full h-full">
    <div
      v-show="loading || loadFailed"
      class="absolute w-full h-full flex justify-center items-center flex-col gap-2"
    >
      <!-- What we show while rive is loading -->
      <slot name="loading" :loading="loading">
        <template v-if="loading && loadingIndicator">
          <LoadingIndicator/>
          <span>{{ loadingText }}</span>
        </template>
      </slot>

      <!-- What we show when rive fails -->
      <slot name="loadFailed" :loadFailed="loadFailed"/>
      <div v-if="loadFailed && !$slots.loadFailed" class="flex flex-col gap-1 items-center justify-center">
        <div class="font-bold text-xl">Ooops! something went wrong</div>
        <div class="text-center text-sm text-navy">Our support team has been notified</div>
        <slot name="loadFailedAction"/>
      </div>
    </div>

    <a v-if="href" :href="href" class="w-full h-full absolute top-0 left-0" @touchend="onTouchEnd">
      <canvas ref="canvas" style="vertical-align: top;" class="w-full h-full"/>
    </a>
    <canvas v-else ref="canvas" style="vertical-align: top;" class="w-full h-full"/>
  </section>
</template>
